/* Batch 34 · B34-8 — on the lead sheet: a reviewer-signed "no further substantive work" reason. With the lead-sheet conclusion
   saved it counts as the head's substantive work for the lock check. Reviewer / Manager / Partner sign and withdraw. */
import React, { useCallback, useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTimeFirm } from "@/lib/dt";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function NoFurtherWorkPanel({ engId, fsHead, disabled }) {
  const { t, lang } = useI18n();
  const [state, setState] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const url = `/engagements/${engId}/lead-sheets/${fsHead}/no-further-work`;
  const load = useCallback(async () => {
    if (!engId || !fsHead) return;
    try { const r = await api.get(url); setState(r.data); setReason(r.data?.waiver?.reason || ""); } catch { setState(null); }
  }, [engId, fsHead, url]);
  useEffect(() => { load(); }, [load]);
  if (!state) return null;
  const err = (e) => toast.error((lang === "ar" && e?.response?.data?.detail_ar) || toErrMsg(e, t("b34a.saveFailed")));
  const sign = async () => {
    setBusy(true);
    try { await api.put(url, { reason }); toast.success(t("b34a.nfwSaved")); await load(); } catch (e) { err(e); } finally { setBusy(false); }
  };
  const withdraw = async () => {
    setBusy(true);
    try { await api.delete(url); await load(); } catch (e) { err(e); } finally { setBusy(false); }
  };
  const w = state.waiver;
  return (
    <div className="rounded-md border p-3 text-xs" data-testid="lead-nfw-panel">
      <div className="font-medium">{t("b34a.nfwTitle")}</div>
      <div className="mb-2 text-muted-foreground">{t("b34a.nfwHelp")}</div>
      <label className="block text-[11px]" htmlFor="lead-nfw-reason">{t("b34a.nfwReason")}</label>
      <textarea id="lead-nfw-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                disabled={disabled || !state.can_sign || busy}
                className="w-full rounded border px-2 py-1 text-xs dark:bg-slate-900" data-testid="lead-nfw-reason" />
      {w && (
        <div className="mt-1 text-[11px] text-emerald-700" data-testid="lead-nfw-signed">
          {t("b34a.nfwSignedBy").replace("{name}", w.signed_by_name || "").replace("{date}", fmtDateTimeFirm(w.signed_at))}
        </div>
      )}
      {!state.can_sign && <div className="mt-1 text-[11px] text-muted-foreground">{t("b34a.nfwOnlyReviewer")}</div>}
      {state.can_sign && (
        <div className="mt-2 flex gap-2">
          <Button size="sm" disabled={disabled || busy || !reason.trim()} onClick={sign} data-testid="lead-nfw-sign">{t("b34a.nfwSign")}</Button>
          {w && <Button size="sm" variant="outline" disabled={disabled || busy} onClick={withdraw} data-testid="lead-nfw-withdraw">{t("b34a.nfwWithdraw")}</Button>}
        </div>
      )}
    </div>
  );
}
